import { Download, Share, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInstallPrompt } from '@/hooks';
import { cn } from '@/lib';
import { Button } from './Button';

/** Safari fires no install event, so iOS users get the menu path spelled out. */
function IosSteps({ variant = 'admin' }: { variant?: 'admin' | 'worker' }) {
  const { t } = useTranslation('common');
  const mutedClass =
    variant === 'worker' ? 'text-worker-text-secondary' : 'text-admin-text-secondary';
  const textClass = variant === 'worker' ? 'text-worker-text' : 'text-admin-text';

  return (
    <div className="mt-2" data-testid="install-ios-steps">
      <p className={cn('text-xs', mutedClass)}>{t('install.iosIntro')}</p>
      <ol className={cn('mt-2 list-inside list-decimal space-y-1 text-xs', textClass)}>
        <li>
          <Share className={cn('mr-1 inline-block h-3.5 w-3.5 align-text-bottom', mutedClass)} />
          {t('install.iosStep1')}
        </li>
        <li>{t('install.iosStep2')}</li>
        <li>{t('install.iosStep3')}</li>
      </ol>
    </div>
  );
}

/**
 * The unsolicited install offer, shown above the auth forms. A banner rather
 * than a modal: the worker who opened the link to log in must be able to do so
 * without clearing anything out of the way first.
 */
export function InstallBanner() {
  const { t } = useTranslation('common');
  const { isOfferOpen, canPrompt, install, dismiss } = useInstallPrompt();

  if (!isOfferOpen) return null;

  return (
    <div
      className="w-full max-w-sm rounded-xl border border-admin-border bg-admin-surface p-4"
      data-testid="install-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-admin-text">{t('install.title')}</p>
          <p className="mt-1 text-xs text-admin-text-secondary">{t('install.description')}</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('install.dismiss')}
          className="rounded-lg p-1.5 text-admin-text-secondary transition-colors hover:bg-admin-surface-hover hover:text-admin-text"
          data-testid="install-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {canPrompt ? (
        <Button size="sm" onClick={install} className="mt-3" data-testid="install-banner-action">
          <Download className="h-4 w-4" />
          {t('install.action')}
        </Button>
      ) : (
        <IosSteps />
      )}
    </div>
  );
}

/**
 * The standing entry point in settings. Unlike the banner it survives a
 * dismissal and a desktop browser, so "Ne sada" is never a final answer.
 *
 * It must not hang on `beforeinstallprompt` either: a load can genuinely go
 * without it (app already installed, browser without support, install criteria
 * unmet), and a load without it left this section rendering nothing at all.
 * With no event to replay, the browser menu is the way in.
 */
export function InstallSection({
  variant = 'admin',
  className,
}: {
  variant?: 'admin' | 'worker';
  className?: string;
}) {
  const { t } = useTranslation('common');
  const { isInstalled, canPrompt, needsIosSteps, install } = useInstallPrompt();
  const [showSteps, setShowSteps] = useState(false);

  if (isInstalled) return null;

  const textClass = variant === 'worker' ? 'text-worker-text' : 'text-admin-text';
  const mutedClass =
    variant === 'worker' ? 'text-worker-text-secondary' : 'text-admin-text-secondary';

  return (
    <div className={cn('space-y-2', className)} data-testid="install-section">
      <div className="flex items-center gap-2">
        <Download className={cn('h-4 w-4', mutedClass)} />
        <h2 className={cn('text-sm font-semibold', textClass)}>{t('install.sectionTitle')}</h2>
      </div>
      <p className={cn('text-xs', mutedClass)}>{t('install.description')}</p>

      {canPrompt ? (
        <Button size="sm" onClick={install} data-testid="install-section-action">
          {t('install.action')}
        </Button>
      ) : needsIosSteps ? (
        showSteps ? (
          <IosSteps variant={variant} />
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowSteps(true)}
            data-testid="install-section-action"
          >
            {t('install.iosHowTo')}
          </Button>
        )
      ) : (
        <p className={cn('text-xs', mutedClass)} data-testid="install-section-hint">
          {t('install.menuHint')}
        </p>
      )}
    </div>
  );
}
